import afterSaveAssociationReduxHookUtil from 'modules/redux/utils/hooks/after-save-association'
import getDomainListPathReduxUtil from 'modules/redux/utils/get-domain-list-path'
import getIdReduxUtil from 'modules/redux/utils/get-id'
import getLocalIdReduxUtil from 'modules/redux/utils/get-local-id'
import getPathReduxUtil from 'modules/redux/utils/get-path'
import getSortKeyReduxUtil from 'modules/redux/utils/get-sort-key'
import getValueReduxUtil from 'modules/redux/utils/get-value'
import isNewReduxUtil from 'modules/redux/utils/is-new'
import isPointerReduxUtil from 'modules/redux/utils/is-pointer'
import setUtil from 'utils/set'
import getDefaultDomainListPath from 'modules/redux/utils/get-default-domain-list-path'
import getReferencesSelector from 'modules/training/modules/references/selectors/get'
import { REFERENCES_DOMAIN } from 'modules/training/modules/references/constants'
import { REDUX_CLASS_NAME_TO_DOMAIN } from 'modules/redux/constants'
import getInstancesFromDomainMapReduxUtil from 'modules/redux/utils/legacy/get-instances-from-domain-map'
import REDUX_OPTION from 'modules/redux/constants/option.js'
import {
  getObjectUtil,
  omitObjectUtil,
  uniqArrayUtil
} from 'packages/src'
import sortByArrayUtil from 'packages/src/modules/array/sort-by/index.js'

/**
 * Gets the ordered instances.
 * @private
 * @param   {Object} data
 *          {Instance[]} instances
 *          {String|null} sortKey
 * @returns {Instance[]}
 */
function getSortedInstances (data) {
  const {
    instances,
    sortKey
  } = data
  return sortByArrayUtil(instances, instance => {
    const value = sortKey
      ? getValueReduxUtil(instance, sortKey)
      : getIdReduxUtil(instance)
    if (typeof value === 'string') {
      return value.toLowerCase()
    }
    if (value < 0 || sortKey) {
      return value
    }
    return -1 * value
  })
}

/**
 * Adds a instance to the DomainList(s).
 * NOTE: If the DomainList key is `all`, the instance will also be added to
 * the categorized DomainLists.
 * e.g. `state.articles.${category}` => `state.articles.nutrition`
 * @private
 * @param   {Object} params
 *          {Object} state
 *          {Instance} instance
 *          {string} domainListPath
 *          {string} [domainPath]
 * @returns {Object}
 */
function addToDomainList (params) {
  const {
    state,
    instance,
    domainListPath,
    existingInstance
  } = params
  // Get the DomainList
  const domainList = getObjectUtil(state, domainListPath)
  const domainListData = getObjectUtil(domainList, 'data')
  const domainPath = params.domainPath || REDUX_CLASS_NAME_TO_DOMAIN[instance.className]
  const domain = getObjectUtil(state, domainPath)
  const currentInstances = getInstancesFromDomainMapReduxUtil(domain.data, domainListData)
  // Add the instance to the DomainList
  const sortKey = getSortKeyReduxUtil(domainPath, domainListPath)
  const newInstances = getSortedInstances({
    instances: [
      ...currentInstances,
      instance
    ],
    sortKey
  })
  // Get the instance keys
  let newDomainListData = newInstances.map(getIdReduxUtil)
  if (existingInstance && isNewReduxUtil(existingInstance)) {
    const existingId = getIdReduxUtil(existingInstance)
    newDomainListData = newDomainListData.filter(id => (
      id !== existingId
    ))
  }
  // Add the instance to the DomainList
  const newDomainList = setUtil(domainList, 'data', uniqArrayUtil(newDomainListData))
  // Update the DomainList
  return setUtil(state, domainListPath, newDomainList)
}

function addToDomainLists (data) {
  let {
    state,
    existingInstance,
    instance,
    domainListPath,
    domainPath
  } = data

  const defaultDomainListPath = getDefaultDomainListPath(instance, domainPath)
  state = addToDomainList({
    state,
    instance,
    domainListPath: defaultDomainListPath,
    existingInstance,
    domainPath
  })

  if (!domainListPath || domainListPath === defaultDomainListPath) {
    domainListPath = getDomainListPathReduxUtil(instance)
  }
  if (domainListPath) {
    state = addToDomainList({
      state,
      instance,
      domainListPath,
      existingInstance,
      domainPath
    })
  }
  return state
}

/**
 * Merges the given `newInstance` inside the `instance`,
 * by only setting the `newInstance` data that was updated.
 * @param  {Instance} instance
 * @param  {Instance} newInstance
 * @return {Instance}
 */
function mergeReduxUtil (instance, newInstance) {
  const newKeys = Object.keys(newInstance.data)
  newKeys.forEach(key => {
    const value = newInstance.data[key]
    if (value !== instance.data[key]) {
      instance = setUtil(instance, `data.${key}`, value)
    }
  })
  return {
    ...instance,
    status: newInstance.status,
    changed: {
      ...omitObjectUtil(instance.changed, newKeys),
      ...newInstance.changed
    },
    updatedAt: (new Date()).toISOString(),
    error: null,
    validation: null
  }
}

function triggerAfterSaveAssociationHook (params) {
  const {
    instance,
    reference,
    updatedState
  } = params
  // If it's a instance referencing the `instance`
  // run its after save association
  // e.g. `reference` = 'posts.data.-3.changed.attachment'
  const referenceArr = reference.split('.')
  if (referenceArr.length !== 5) {
    return updatedState
  }
  const referenceInstance = getObjectUtil(updatedState, referenceArr.slice(0, 3).join('.'))
  if (!referenceInstance) {
    console.warn('referenceInstance does not exist')
    return updatedState
  }
  return afterSaveAssociationReduxHookUtil(referenceInstance, updatedState, {
    associationKey: referenceArr[referenceArr.length - 1],
    association: instance
  })
}

function updateReferenceInstance (params) {
  let {
    state,
    instance,
    oldPath,
    existingInstance
  } = params
  const references = getReferencesSelector(oldPath)(state)
  const id = getIdReduxUtil(instance)
  // // console.debug('updateReferenceInstance: oldPath ', oldPath)
  // // console.debug('updateReferenceInstance: oldReferences ', references)
  state = references.reduce((updatedState, reference) => {
    let referenceValue = getObjectUtil(updatedState, reference)
    // // console.debug('updateReferenceInstance: reference ', reference)
    // // console.debug('updateReferenceInstance: referenceValue ', referenceValue)
    if (!referenceValue) {
      return updatedState
    }
    updatedState = triggerAfterSaveAssociationHook({
      updatedState,
      reference,
      instance
    })
    // Update the pointer
    if (Array.isArray(referenceValue)) {
      referenceValue = referenceValue.map(element => {
        if (getIdReduxUtil(element) === getLocalIdReduxUtil(existingInstance)) {
          return {
            ...element,
            data: {
              id
            }
          }
        }
        return element
      })
    } else if (isPointerReduxUtil(referenceValue)) {
      referenceValue = {
        ...referenceValue,
        data: {
          id
        }
      }
    } else {
      referenceValue = id
    }
    // // console.debug('updateReferenceInstance: referenceValue after ', referenceValue)
    return setUtil(updatedState, reference, referenceValue)
  }, state)
  const referencesDomain = getObjectUtil(state, REFERENCES_DOMAIN)
  const newReferencesDomain = omitObjectUtil(referencesDomain, [oldPath])
  return setUtil(state, REFERENCES_DOMAIN, newReferencesDomain)
}

function mergeOldAndNewInstance (params) {
  let {
    state,
    existingInstance,
    oldPath,
    instance
  } = params
  // Remove the old instance
  state = setUtil(state, oldPath, undefined)
  // Remove references from the old instance and make them point to the new one
  return updateReferenceInstance({
    state,
    instance,
    oldPath,
    existingInstance
  })
}

/**
 * Add the `newInstance` to the state.
 * @private
 * @param   {Object} params
 *          {Object} params.state
 *          {Instance} params.instance
 *          {Instance|null} params.existingInstance
 *          {string} [params.domainPath]
 * @returns {Object}
 */
function addInstance (params) {
  let {
    state,
    instance,
    existingInstance,
    domainPath,
    options,
    path
  } = params
  const newPath = path || getPathReduxUtil(instance, domainPath)
  // The existingInstance doesn't exist, set it
  if (!existingInstance) {
    return setUtil(state, newPath, instance)
  }
  const oldPath = path || getPathReduxUtil(existingInstance, domainPath)
  // Merge the old existingInstance with the new one
  existingInstance = mergeReduxUtil(existingInstance, instance)
  // Update the references pointing to the old existingInstance to the new one
  if (oldPath !== newPath) {
    state = mergeOldAndNewInstance({
      state,
      instance,
      oldPath,
      existingInstance
    })
  }
  if (options?.[REDUX_OPTION.RESET_CHANGED]) {
    existingInstance.changed = {}
  }
  return setUtil(state, newPath, existingInstance)
}

/**
 * Adds the instance to the given path.
 * @param   {Object}  params
 *          {Object} state
 *          {Instance[]} instance
 *          {string} [domainListPath]
 *          {string} [domainPath]
 * @returns {Object}
 */
function addReduxUtil (params) {
  let {
    state,
    instance,
    existingInstance,
    domainListPath,
    domainPath,
    options,
    path
  } = params

  // Add the instance
  state = addInstance({
    state,
    instance,
    existingInstance,
    domainPath,
    options,
    path
  })

  // Add the instance to the DomainList
  state = addToDomainLists({
    state,
    instance,
    domainListPath,
    existingInstance,
    domainPath
  })
  return state
}

export default addReduxUtil
